import { Alert, Breadcrumb, Button, Col, Drawer, Flex, Form, Input, Layout, notification, Popconfirm, Row, Select, Skeleton, Space, Table, Tag, theme, Typography } from 'antd';
import { FilterOutlined, HomeOutlined, DeleteOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react'

import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSelector } from 'react-redux';
import MeetingAPI from '../../../restAPI/MeetingAPI';
import MasterDataAPI from '../../../restAPI/MasterDataAPI';
import CommonSkeletonLoader from '../../../helperComponents/CommonSkeleton';

const { Text, Link } = Typography;

export default function AltSignatory({ props }) {
    const { token: { colorBgContainer, borderRadiusLG, colorBorder, colorBgBase, colorPrimary, colorBgElevated } } = theme.useToken();
    const { companyId, token, userCompany, role } = useSelector(state => state.auth);
    const [fullScreenLoading, setFullScreenLoading] = useState(false)
    const [open, setOpen] = useState(false);
    const [altSignatoryList, setaltSignatoryList] = useState([]);
    const [userListForBranch, setUserListForBranch] = useState([]);
    const [dataSourse, setDataSourse] = useState([]);
    const [initialAltSignatoryList, setInitialAltSignatoryList] = useState([]);
    const [showUpdateButton, setShowUpdateButton] = useState(false);
    const [branchList, setBranchList] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [showPopconfirm, setShowPopconfirm] = useState(false);
    const [currentCompanyInfo, setCurrentCompanyInfo] = useState({})
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [isStillDeleting, setIsStillDeleting] = useState(false);

    const [form] = Form.useForm();
    const [api, contextHolder] = notification.useNotification();

    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };
    useEffect(() => {
        const companyInfo = userCompany.companyData.find(company => company.companyId == companyId);
        setCurrentCompanyInfo(companyInfo);
    }, [companyId])

    const getSignatoriesListByUserId = async () => {
        await MeetingAPI.getSignatoriesByUserId(
            currentCompanyInfo.userDetailsId,
            companyId,
            token
        )
            .then((res) => {
                if (res.data) {
                    const signatories = res.data.map((item, index) => ({
                        key: item.id,
                        priority: item.priority,
                        signatoryId: item.signatoryUser ? item.signatoryUser.id : "N/A",
                        signatoryName: item.signatoryUser
                            ? item.signatoryUser.name
                            : "N/A",
                        comment: item.comment,
                    }));
                    signatories.sort((a, b) => a.priority - b.priority);
                    setaltSignatoryList(signatories);
                    setInitialAltSignatoryList(signatories);
                    setFullScreenLoading(false);
                } else {
                    setaltSignatoryList([]);
                    setInitialAltSignatoryList([]);
                    setFullScreenLoading(false);
                }
            })
            .catch((error) => {
                setFullScreenLoading(false);
                setaltSignatoryList([]);
                setInitialAltSignatoryList([]);
                if (error.response && error.status !== 404) {
                    openNotificationWithIcon(
                        "error",
                        "Failed to retrieve",
                        `${error.response.data.message}`,
                        "bottomLeft"
                    );
                } else {
                    openNotificationWithIcon(
                        "error",
                        "Failed to retrieve",
                        `Failed to get alternative signatory list.`,
                        "bottomLeft"
                    );
                }
            });
    };

    const getBranchList = async () => {
        await MasterDataAPI.getBranchDataByCompanyId(companyId, token)
            .then((res) => {
                if (res) {
                    const activeBranches = res?.data ? res?.data.filter(branch => branch.status).map(singleBranch => {
                        return { label: singleBranch.name, value: singleBranch.id };
                    }) : [];
                    setBranchList(activeBranches);
                }
            })
            .catch((err) => {
                console.log("Error :", err.message);
            })
    }

    const getUserListByBranchId = async () => {
        setIsUsersLoading(true)
        try {
            const res = await MasterDataAPI.getUserListByBranchId(selectedBranchId, companyId, token);

            if (res && res.data) {
                const userList = res.data
                    .filter(singleUser =>
                        !(Array.isArray(altSignatoryList) && altSignatoryList.some(signatory => signatory.signatoryId === singleUser.id))
                        && singleUser.id !== currentCompanyInfo.userDetailsId
                    )
                    .map(singleUser => {
                        return { label: singleUser.name, value: singleUser.id };
                    });

                if (userList && userList.length === 0) {
                    openNotificationWithIcon('info', 'User List empty', `No users available for the selected branch.`, 'bottomLeft');
                }
                setUserListForBranch(userList);
                setIsUsersLoading(false)
            } else {
                setUserListForBranch([]);
                setIsUsersLoading(false)
            }
        } catch (error) {
            setShowPopconfirm(false);
            setIsUsersLoading(false)
            if (error.response) {
                openNotificationWithIcon('error', 'Failed to retrieve', `${error.response.data.message}`, 'bottomLeft');
            } else {
                openNotificationWithIcon('error', 'Failed to retrieve', `${error.message}`, 'bottomLeft');
            }
            console.error("Error in getUserListByBranchId:", error);
        }
    };

    const showDrawer = () => {
        setOpen(true);
    };
    const onClose = () => {
        setSelectedBranchId(null);
        setShowPopconfirm(false);
        form.resetFields();
        setUserListForBranch([]);
        setOpen(false);
    };

    const Row = (props) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
            id: props['data-row-key'],
        });
        const style = {
            ...props.style,
            transform: CSS.Translate.toString(transform),
            transition,
            cursor: 'move',
            ...(isDragging
                ? {
                    position: 'relative',
                    zIndex: 9,
                }
                : {}),
        };
        return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
                distance: 1,
            },
        }),
    );

    const onDragEnd = ({ active, over }) => {
        if (active.id !== over?.id) {
            const newList = arrayMove(
                altSignatoryList,
                altSignatoryList.findIndex(i => i.key === active.id),
                altSignatoryList.findIndex(i => i.key === over?.id)
            );

            newList.forEach((item, index) => {
                item.priority = index + 1;
            });

            setaltSignatoryList(newList);
            setShowUpdateButton(JSON.stringify(newList) !== JSON.stringify(initialAltSignatoryList));
        }
    };

    const validateForm = async () => {
        try {
            const values = await form.validateFields();
            return values;
        } catch (error) {
            if (error.errorFields) {
                // openNotificationWithIcon('error', 'Validation Failed', 'Please fill the whole form.', 'bottomLeft');
            }
            // Return a rejection to stop further processing in handleSubmit
            return Promise.reject('Validation failed');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await validateForm();

            if (!values) {
                return;
            }

            if (altSignatoryList.length > 5) {
                setShowPopconfirm(false);
                openNotificationWithIcon('error', 'Failed to add', `Can't have more than 5 alternative signatories.`, 'bottomLeft');
                onClose();
                return;
            }

            if (values.name === currentCompanyInfo.userDetailsId) {
                setShowPopconfirm(false);
                openNotificationWithIcon('error', 'Failed to add', `Can't add same user for alternative signatory`, 'bottomLeft');
                return;
            }

            const newSignatory = {
                priority: altSignatoryList.length + 1, // Adding at the end of the list
                signatoriesId: values.name,
                userDetailsId: currentCompanyInfo.userDetailsId,
                companyId: companyId
            };

            const response = await MeetingAPI.addSignatory(newSignatory, companyId, token);

            if (response.data) {
                getSignatoriesListByUserId();
                openNotificationWithIcon('success', 'Success', 'Alternative signatory added successfully.', 'bottomLeft');
                onClose();
            }
        } catch (error) {
            setShowPopconfirm(false);
            if (error !== 'Validation failed') {
                if (error.response) {
                    openNotificationWithIcon('error', 'Failed to add', `${error.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Failed to add', `${error.message}`, 'bottomLeft');
                }
            }
            console.log("Error in handleSubmit:", error);
        }
    };

    const handleUpdatePriorities = async (newList = altSignatoryList) => {
        try {
            const formattedData = {
                priorityUpdates: newList.map((item) => ({
                    signatoriesId: item.signatoryId,
                    priority: item.priority
                }))
            };

            const response = await MeetingAPI.updateAltSignatoryPriorityLevels(formattedData, companyId, token);

            if (response.status === 200) {
                setTimeout(async () => {
                    await getSignatoriesListByUserId();
                    setInitialAltSignatoryList(newList);
                    setShowUpdateButton(false);
                    setShowPopconfirm(false);
                    openNotificationWithIcon('success', 'Success', `Priorities update successfully.`, 'bottomLeft');
                }, 500);
            } else {
                openNotificationWithIcon('error', 'Update failed', 'Priorities update failed.', 'bottomLeft');
            }
        } catch (error) {
            openNotificationWithIcon('error', 'Update failed', 'Priorities update failed.', 'bottomLeft');
            console.log("error in handleUpdatePriorities :", error);
        }
    }

    const handleUpdatePrioritiesAfterDelete = async (newList = altSignatoryList) => {
        try {
            const formattedData = {
                priorityUpdates: newList.map((item) => ({
                    signatoriesId: item.signatoryId,
                    priority: item.priority
                }))
            };

            const response = await MeetingAPI.updateAltSignatoryPriorityLevels(formattedData, companyId, token);

            if (response.status === 200) {
                setTimeout(async () => {
                    await getSignatoriesListByUserId()
                    setInitialAltSignatoryList(newList);
                    setShowUpdateButton(false);
                    setShowPopconfirm(false);
                    openNotificationWithIcon('success', 'Success', `Aternative signatory delete successfully.`, 'bottomLeft');
                }, 500)
            } else {
                openNotificationWithIcon('error', 'Update failed', 'Aternative signatory delete failed.', 'bottomLeft');
            }
        } catch (error) {
            openNotificationWithIcon('error', 'Update failed', 'Aternative signatory delete failed.', 'bottomLeft');
            console.log("error in handleUpdatePriorities :", error);
        }
    }

    const handleCancelPrioritiesChanges = () => {
        getSignatoriesListByUserId();
        setShowUpdateButton(false);
    }

    const handleSignatoryRemove = async (record) => {
        setIsStillDeleting(true)
        try {
            const res = await MeetingAPI.deleteAlternativeSignatory(record.signatoryId, companyId, token);
            if (res.status === 200) {
                const newDataList = altSignatoryList
                    .filter(signatory => signatory.signatoryId !== record.signatoryId)
                    .map((signatory, index) => ({ ...signatory, priority: index + 1 }));

                setaltSignatoryList(newDataList);
                await handleUpdatePrioritiesAfterDelete(newDataList);
            }
            else {
                openNotificationWithIcon('error', 'Failed to remove', `You can't remove selected signatory`, 'bottomLeft');
            }
        } catch (error) {
            if (error.response.data.message) {
                openNotificationWithIcon('error', 'Failed to remove', `${error.response.data.message}`, 'bottomLeft');
            }
            else {
                openNotificationWithIcon('error', 'Failed to remove', `You can't remove selected signatory`, 'bottomLeft');
            }
            console.log("Error in handleSignatoryRemove ")
        } finally {
            setIsStillDeleting(false);
        }
    }

    const handleBranchChange = (value) => {
        form.setFieldsValue({ name: undefined })
        setUserListForBranch([]);
        setSelectedBranchId(value);
    }

    const handlePopConfirm = async (e) => {
        e.preventDefault();
        try {
            const values = await validateForm();
            if (!values) {
                setShowPopconfirm(false)
                return;
            } else {
                handleSubmit();
            }
        } catch (error) {

        }
    }

    useEffect(() => {
        if (currentCompanyInfo?.userDetailsId) {
            setFullScreenLoading(true);
            getSignatoriesListByUserId();
            getBranchList();
        }
    }, [currentCompanyInfo]);

    useEffect(() => {
        if (selectedBranchId) {
            getUserListByBranchId();
        }
    }, [selectedBranchId])

    useEffect(() => {
        if (altSignatoryList.length > 0) {
            setDataSourse(altSignatoryList)
        }
    }, [altSignatoryList])

    const columns = [
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
        },
        {
            title: 'Signatory Name',
            dataIndex: 'signatoryName',
            key: 'signatoryName',
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            render: (_, record) => (
                <Flex gap="small" justify="center" align="center" vertical>
                    <Flex gap="middle" wrap="wrap">
                        <Popconfirm
                            title="Do you want to delete this alternative signatory?"
                            onConfirm={async () => {
                                await handleSignatoryRemove(record)
                            }}
                            onCancel={() => openNotificationWithIcon('error', 'Abort', `Delete cancelled`, 'bottomLeft')}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                type="primary"
                                icon={<DeleteOutlined />}
                                size='small'
                                disabled={showUpdateButton || isStillDeleting}
                            />
                        </Popconfirm>

                    </Flex>
                </Flex>
            ),
        },
    ];

    return (
        <>
            <Flex gap={20} justify='space-between' align='center' style={{
                margin: '16px',
                padding: '16px 24px',
                // minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}>
                <Breadcrumb
                    items={[
                        {
                            // href: '',
                            title: <HomeOutlined />,
                        },
                        {
                            title: 'Meeting Management',
                        },
                        {
                            title: 'Alternative Signatory',
                        },
                    ]}
                />
                <Flex justify='center' align='center' gap={48}>
                    <Button type="primary" onClick={showDrawer} >Add New</Button>
                </Flex>

            </Flex>
            <div style={{
                margin: '16px',
            }}>
            </div>
            {fullScreenLoading ? <CommonSkeletonLoader itemCount={2} /> : (<>
                <div style={{
                    margin: '16px',
                    padding: 24,
                    minHeight: "78svh",
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}>
                    <Alert message="If you need to change the priority order, Please drag and change the row order." type="info" showIcon closable style={{ marginBottom: 8 }} />
                    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                        <SortableContext
                            // rowKey array
                            items={altSignatoryList.map((i) => i.key)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Table
                                components={{
                                    body: {
                                        row: Row,
                                    },
                                }}
                                rowKey="key"
                                columns={columns}
                                dataSource={altSignatoryList}
                                scroll={{ x: 'max-content' }}
                                pagination={{
                                    pageSize: 5,
                                    hideOnSinglePage: true,
                                }}
                            />
                        </SortableContext>
                    </DndContext>
                </div>
            </>)}
            <Drawer title="Add Alternative Signatory"
                onClose={onClose}
                open={open}
                // placement='bottom'
                footer={[
                    <Flex justify='flex-end' align='center' gap='large'>
                        <Button onClick={onClose}>
                            Cancel
                        </Button>

                        <Popconfirm
                            title={`Do you want to add the alternative signatory?`}
                            open={showPopconfirm}
                            onConfirm={() => handleSubmit()}
                            onCancel={() => {
                                setShowPopconfirm(false)
                                openNotificationWithIcon('error', 'Abort', `Cancelled`, 'bottomLeft')
                            }}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="primary" onClick={handlePopConfirm}>
                                Save
                            </Button>
                        </Popconfirm>
                    </Flex>
                ]
                }
            >
                {
                    isUsersLoading ? <CommonSkeletonLoader itemCount={3} /> : (
                        <Form layout="vertical" form={form}>
                            <Form.Item label="Branch" name='branch' rules={[{ required: true, message: 'Please select a branch!' }]}>
                                <Select
                                    placeholder="Select a branch"
                                    options={branchList}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    onChange={(value) => handleBranchChange(value)}
                                />
                            </Form.Item>

                            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please select a user!' }]}>
                                <Select
                                    placeholder="Select a user"
                                    options={userListForBranch}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                />
                            </Form.Item>

                        </Form>
                    )
                }
            </Drawer>
            {contextHolder}
            {
                showUpdateButton && (
                    <Flex justify='space-between' align='center' gap={12} style={{
                        paddingLeft: 8,
                        paddingRight: 16,
                        backgroundColor: colorBgElevated,
                        position: 'absolute',
                        bottom: 0,
                        width: 'calc(100% - 80px)',
                        borderTop: `1px solid ${colorBorder}`,
                        paddingTop: 16,
                        paddingBottom: 16,
                    }}>
                        <div> <Text>You have changes in priority order</Text></div>
                        <Flex justify='space-around' align='center' gap={12}>
                            <Popconfirm
                                title={`Do you want to update the priority list?`}
                                onConfirm={() => {
                                    handleUpdatePriorities()
                                }}
                                onCancel={() => {
                                    getSignatoriesListByUserId();
                                    setShowUpdateButton(false);
                                    setShowPopconfirm(false)
                                    openNotificationWithIcon('error', 'Abort', `Cancelled`, 'bottomLeft')
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type='primary'>Save</Button>
                            </Popconfirm>
                            <Button type='default' onClick={() => handleCancelPrioritiesChanges()}>Cancel</Button>
                        </Flex>
                    </Flex>
                )
            }
        </>
    )
}